import { CommonNodeViewWrapper } from '@/components/common/node-view-wrapper';
import { LazyMountIframe, useLazyMountIframe } from '@/components/tiptap-editor/tiptap-extension/lazy-mount-tool-iframe';
import { ZyEditToolButton } from '@/components/tiptap-editor/tiptap-ui/zyEditTool-button';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useIsEditorReadonly } from '@/hooks/use-is-editor-readonly';
import { useNodeViewCollaborationPresence } from '@/hooks/use-node-view-collaboration-presence';
import type { AttributeDef } from '@/lib/parseZyXML';
import { parseZyXML } from '@/lib/parseZyXML';
import { applyToolXmlUpdatesWithImageSync } from '@/lib/sync-tool-builder-images';
import { DEFAULT_EDITOR_OPTIONS } from '@/lib/utils';
import { Editor as MonacoEditor } from '@monaco-editor/react';
import { type ReactNodeViewProps } from '@tiptap/react';
import { connectToChild } from 'penpal';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import {
    buildReferencePopupUrl,
    createReferenceInitMessage,
    isZyAnimatorReferenceReadyMessage,
    openReferencePopup,
} from './zyanimator-reference-popup';
import { ZyAnimatorVersionConversionButton } from './zyanimator-version-conversion-button';
import { ZyAnimatorVersionConversionDialog } from './zyanimator-version-conversion-dialog';

interface ChildIframeConnection {
    setIsEditing?: (isEditing: boolean) => Promise<void>;
    beginConversionSession?: (originalXml: string) => Promise<void>;
    cancelConversionSession?: () => Promise<void>;
    getLatestConversionXML?: () => Promise<string>;
    destroy: () => Promise<void>;
}

const parseZyXMLConfig = {
    rootTag: 'zyAnimator',
    xmlPropName: 'animXMLToLoad',
    attributes: [
        { name: 'caption', type: 'string' },
        { name: 'height', type: 'number' },
        { name: 'width', type: 'number' },
        { name: 'image_ids', type: 'array', xmlName: 'image-ids' },
    ] as AttributeDef[],
    hasAltDescription: true,
};

const ZyAnimatorNodeView: React.FC<ReactNodeViewProps> = ({ node, updateAttributes, selected, editor, decorations }) => {
    const isEditorReadonly = useIsEditorReadonly(editor);
    const { id, animXMLToLoad, caption, animator_version } = node.attrs;
    const url = `${import.meta.env.VITE_ZYBOOKS_URL}/modern-authoring-tools/animation-builder`;
    const referencePopupUrl = `${import.meta.env.VITE_ZYBOOKS_URL}/modern-authoring-tools/animation-player-v1-reference`;
    const zybooksOrigin = new URL(url).origin;

    const [isDevMode, setIsDevMode] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isConverting, setIsConverting] = useState(false);
    const [isStartingConversion, setIsStartingConversion] = useState(false);
    const [iframeHeight, setIframeHeight] = useState<number | null>(null);
    const [showVersionConversionModal, setShowVersionConversionModal] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const [connection, setConnection] = useState<ChildIframeConnection | null>(null);
    const isConvertingRef = useRef(false);
    const referencePopupRef = useRef<Window | null>(null);

    const { shouldMount, isIframeLoaded, handleIframeLoad, resetLoadState } = useLazyMountIframe({
        containerRef,
        editor,
        forceMount: selected || isEditing || isConverting,
        id,
    });
    const { presenceUsers, presenceTracking } = useNodeViewCollaborationPresence(editor, id, 'zyAnimator', isEditing || isConverting);
    const isV1Animator = animator_version === '1';
    const animationLabel = `Animation${isV1Animator ? ' (V1)' : ''}`;
    const isFullscreen = isEditing || isConverting;

    const handleUpdateAnimXML = useDebounceCallback((nextAnimXMLToLoad: string) => {
        const updates = parseZyXML(nextAnimXMLToLoad, parseZyXMLConfig);
        if (!updates) {
            return;
        }
        applyToolXmlUpdatesWithImageSync(editor, String(id), updates, updateAttributes);
    }, 300);

    const closeReferencePopup = useCallback(() => {
        referencePopupRef.current?.close();
        referencePopupRef.current = null;
    }, []);

    const endConversionSession = useCallback(
        async (options?: { cancelOnIframe?: boolean }) => {
            isConvertingRef.current = false;
            setIsConverting(false);
            setIsEditing(false);

            if (options?.cancelOnIframe) {
                await connection?.cancelConversionSession?.();
            }

            await connection?.setIsEditing?.(false);
            closeReferencePopup();
        },
        [closeReferencePopup, connection],
    );

    const handleCancelConversion = useCallback(async () => {
        await endConversionSession({ cancelOnIframe: true });
    }, [endConversionSession]);

    const handleAcceptConversion = useCallback(async () => {
        if (!connection?.getLatestConversionXML) {
            return;
        }

        const latestXml = await connection.getLatestConversionXML();
        const updates = parseZyXML(latestXml, parseZyXMLConfig);
        if (!updates) {
            return;
        }

        updateAttributes({
            ...updates,
            animator_version: '2',
        });

        isConvertingRef.current = false;
        setIsConverting(false);
        setIsEditing(false);
        await connection.setIsEditing?.(false);
        closeReferencePopup();
    }, [closeReferencePopup, connection, updateAttributes]);

    const handleReopenReferencePopup = useCallback(() => {
        const popup = openReferencePopup(buildReferencePopupUrl(referencePopupUrl, window.location.origin));
        if (!popup) {
            return;
        }

        referencePopupRef.current = popup;

        const onMessage = (event: MessageEvent) => {
            if (event.origin !== zybooksOrigin || event.source !== popup) {
                return;
            }

            if (isZyAnimatorReferenceReadyMessage(event.data, event.origin, event.source, popup)) {
                popup.postMessage(createReferenceInitMessage(animXMLToLoad, editor.state.doc.attrs.baseImageUrl ?? ''), zybooksOrigin);
                window.removeEventListener('message', onMessage);
            }
        };

        window.addEventListener('message', onMessage);
    }, [animXMLToLoad, editor.state.doc.attrs.baseImageUrl, referencePopupUrl, zybooksOrigin]);

    const handleStartConversion = useCallback(async () => {
        if (!connection?.beginConversionSession) {
            return;
        }

        setIsStartingConversion(true);

        const popup = openReferencePopup(buildReferencePopupUrl(referencePopupUrl, window.location.origin));
        if (!popup) {
            setIsStartingConversion(false);
            return;
        }

        referencePopupRef.current = popup;

        try {
            await new Promise<void>((resolve, reject) => {
                const timeoutId = window.setTimeout(() => {
                    window.removeEventListener('message', onMessage);
                    reject(new Error('Reference popup did not respond'));
                }, 15000);

                const onMessage = (event: MessageEvent) => {
                    if (event.origin !== zybooksOrigin || event.source !== popup) {
                        return;
                    }

                    if (isZyAnimatorReferenceReadyMessage(event.data, event.origin, event.source, popup)) {
                        popup.postMessage(
                            createReferenceInitMessage(animXMLToLoad, editor.state.doc.attrs.baseImageUrl ?? ''),
                            zybooksOrigin,
                        );
                        window.clearTimeout(timeoutId);
                        window.removeEventListener('message', onMessage);
                        resolve();
                    }
                };

                window.addEventListener('message', onMessage);
            });

            isConvertingRef.current = true;
            setIsConverting(true);
            setShowVersionConversionModal(false);

            await connection.beginConversionSession(animXMLToLoad);
            setIsEditing(true);
            await connection.setIsEditing?.(true);
        } catch {
            closeReferencePopup();
            isConvertingRef.current = false;
            setIsConverting(false);
        } finally {
            setIsStartingConversion(false);
        }
    }, [animXMLToLoad, closeReferencePopup, connection, editor.state.doc.attrs.baseImageUrl, referencePopupUrl, zybooksOrigin]);

    const handleSetIsEditing = (nextIsEditing: boolean) => {
        setIsEditing(nextIsEditing);
        connection?.setIsEditing?.(nextIsEditing);
    };

    const establishConnection = async () => {
        if (isDevMode || !iframeRef.current) {
            return;
        }
        try {
            const connectionObj = connectToChild({
                iframe: iframeRef.current,
                methods: {
                    updateParentXML: (XML: string) => {
                        if (isConvertingRef.current) return;
                        handleUpdateAnimXML(XML);
                    },
                    getParentXML: () => {
                        return animXMLToLoad;
                    },
                    getBaseImageUrl: () => {
                        return editor.state.doc.attrs.baseImageUrl;
                    },
                    getAnimatorVersion: () => {
                        if (isConvertingRef.current) {
                            return '2';
                        }
                        return isV1Animator ? '1' : '2';
                    },
                    notifyEscapeKeyPressed: () => {
                        if (isConvertingRef.current) {
                            void handleCancelConversion();
                            return;
                        }
                        setIsEditing(false);
                    },
                    setIframeHeight: (height: number) => {
                        setIframeHeight(height);
                    },
                },
            });
            const childConnection = await connectionObj.promise;
            setConnection({
                ...childConnection,
                destroy: connectionObj.destroy,
            } as ChildIframeConnection);
        } catch (error) {
            console.error('Failed to establish connection:', error);
            setConnection(null);
        }
    };

    const destroyConnection = async () => {
        if (connection) {
            await connection.destroy();
            setConnection(null);
        }
    };

    useEffect(() => {
        if (!shouldMount || isDevMode) {
            return;
        }

        establishConnection(); // eslint-disable-line react-you-might-not-need-an-effect/no-external-store-subscription

        return () => {
            destroyConnection();
        };
    }, [shouldMount, isDevMode]); // eslint-disable-line react-hooks/exhaustive-deps -- We only want to re-run this effect when shouldMount or isDevMode changes

    useEffect(() => {
        return () => {
            closeReferencePopup();
        };
    }, [closeReferencePopup]);

    useEffect(() => {
        if (!isConverting) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                void handleCancelConversion();
            }
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [handleCancelConversion, isConverting]);

    const handleDevModeChange = (checked: boolean) => {
        resetLoadState();
        setIsDevMode(checked);
    };

    return (
        <CommonNodeViewWrapper
            decorations={decorations}
            label={isFullscreen ? null : animationLabel}
            selected={selected}
            containerClassName={`${isFullscreen ? 'inset-0 z-51 rounded-none' : ''}`}
            contentClassName="mx-0"
            position={isFullscreen ? 'fixed' : 'relative'}
            presenceTracking={presenceTracking}
            presenceUsers={presenceUsers}
        >
            <div>
                <div className="bg-gray-100 px-4 py-2 dark:bg-gray-800 border-b flex items-center justify-between h-[50px] gap-x-6">
                    {!isFullscreen && <div className="text-sm font-medium overflow-hidden text-ellipsis whitespace-nowrap">{caption}</div>}
                    {isConverting && <div className="text-sm font-medium whitespace-nowrap">Convert animation to V2</div>}
                    <div className="flex items-center ml-auto gap-x-2">
                        {isConverting ? (
                            <>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={isEditorReadonly}
                                    onClick={handleReopenReferencePopup}
                                >
                                    Reopen V1 reference
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={isEditorReadonly}
                                    onClick={() => void handleCancelConversion()}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    size="sm"
                                    disabled={isEditorReadonly}
                                    onClick={() => void handleAcceptConversion()}
                                >
                                    Accept
                                </Button>
                            </>
                        ) : (
                            <>
                                {!isEditing && (
                                    <label className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                        Dev mode:
                                        <Switch
                                            checked={isDevMode}
                                            disabled={isEditorReadonly}
                                            onCheckedChange={handleDevModeChange}
                                        />
                                    </label>
                                )}
                                {isV1Animator ? (
                                    <ZyAnimatorVersionConversionButton
                                        disabled={!connection || isDevMode || isEditorReadonly}
                                        onOpenConversionDialog={() => setShowVersionConversionModal(true)}
                                    />
                                ) : (
                                    <ZyEditToolButton
                                        isEditing={isEditing}
                                        onSetIsEditing={handleSetIsEditing}
                                        disabled={!connection || isDevMode || isV1Animator || isEditorReadonly}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </div>
                <div
                    className={isFullscreen ? 'h-[calc(100vh-50px)]' : iframeHeight === null ? 'h-96' : ''}
                    style={!isFullscreen && iframeHeight !== null ? { height: `${iframeHeight}px` } : undefined}
                >
                    {isDevMode ? (
                        <MonacoEditor
                            defaultLanguage="xml"
                            value={animXMLToLoad}
                            onChange={value => handleUpdateAnimXML(value || '')}
                            options={{ ...DEFAULT_EDITOR_OPTIONS, readOnly: isEditorReadonly }}
                            theme="vs"
                        />
                    ) : (
                        <LazyMountIframe
                            containerRef={containerRef}
                            shouldMount={shouldMount}
                            id={id}
                            url={url}
                            title="Animation"
                            iframeRef={iframeRef}
                            loadingMessage="Loading animation…"
                            isIframeLoaded={isIframeLoaded}
                            onLoad={handleIframeLoad}
                            iframeClassName="h-full w-full border-0 bg-zybooks-bg"
                        />
                    )}
                </div>
            </div>
            <ZyAnimatorVersionConversionDialog
                showVersionConversionModal={showVersionConversionModal}
                setShowVersionConversionModal={setShowVersionConversionModal}
                onStartConversion={() => void handleStartConversion()}
                isStarting={isStartingConversion}
            />
        </CommonNodeViewWrapper>
    );
};

export default memo(ZyAnimatorNodeView);
